import * as joint from '@joint/core';
// Register every persisted shape so hydration retains class defaults and methods.
import './entity-nodes';
import './flow-links';
import './drawable-elements';
import {
    buildCapabilityTable,
    classifyCell,
    flowTypeNameForLinkClass,
    type CellKindCapability,
    type SnapshotCellKind,
} from './capability-registry';
import { EntityGroup } from './entity-nodes';
import { Callout, DrawnLine, DrawnRectangle, IconElement, TextBox } from './drawable-elements';
import { tailTipToGraph } from './graph-actions';
import { createGraph, getLinkEndId, type EntityCatalogEntry } from './graph-load';
import { getMergedFlows } from './flow-merge';
import { isIconLabel } from './icons';
import { Node } from './node-base';

// Read hydrated cells because JointJS omits class defaults from toJSON(); raw parsing would miss
// unmodified values.
//
// Inspection shares mutation's loader and attribution backfill without editor compatibility
// cleanup, so snapshot cellIds resolve against the graph a mutation will load.

export const SNAPSHOT_SCHEMA_VERSION = 1;

export interface InspectGraphJsonRequest {
    graphJson: string;
    entityCatalog?: EntityCatalogEntry[];
}

export interface SnapshotCellEntity {
    entityId: string;
    label?: string;
    shape?: string;
    countryCode?: string;
    isExternal?: boolean;
}

export interface SnapshotLinkLabel {
    index: number;
    kind: 'text' | 'icon';
    text?: string;
    iconName?: string;
}

export interface SnapshotPoint {
    x: number;
    y: number;
}

// One flow drawn by an arrow that draws several, identified by the entities it runs between: it
// has no cell of its own, because the arrow is the cell. Always fully attributed — only fully
// attributed flows can be merged in the first place.
export interface SnapshotFlowPair {
    sourceEntityId: string;
    targetEntityId: string;
    stepId?: string;
}

// One flat cell shape; only the fields relevant to the cell's kind are populated, and
// JSON.stringify drops the undefined rest.
export interface SnapshotCell {
    cellId: string;
    kind: SnapshotCellKind;
    cellType: string;
    position?: SnapshotPoint;
    size?: { width: number; height: number };
    entityIds?: string[];
    entities?: SnapshotCellEntity[];
    primaryLabel?: string;
    secondaryLabel?: string;
    text?: string;
    backgroundColor?: string;
    backgroundColor2?: string;
    textColor?: string;
    borderColor?: string;
    groupId?: string;
    groupLabel?: string;
    isManagedGroup?: boolean;
    isLinkedToProjectEntities?: boolean;
    iconName?: string;
    shapeColor?: string;
    tailTip?: SnapshotPoint;
    tailTargetCellId?: string;
    strokeColor?: string;
    strokeWidth?: number;
    flowType?: string | null;
    color?: string;
    sourceCellId?: string;
    targetCellId?: string;
    sourcePoint?: SnapshotPoint;
    targetPoint?: SnapshotPoint;
    // The flow this arrow draws, when it draws exactly one — which includes every arrow that is
    // only partly attributed (a TextBox endpoint leaves one side absent) or not attributed at all.
    // Mutually exclusive with representedFlows: an arrow drawing several flows has no primary one.
    sourceEntityId?: string;
    targetEntityId?: string;
    stepId?: string;
    hidden?: boolean;
    // Every flow this arrow draws, when it draws more than one. They are peers — none is the
    // arrow's "own" flow — and each retains its own step id for state-aware enrichment in C#.
    // Present only in the merged case; arrow-level endpoint and step fields are absent then.
    representedFlows?: SnapshotFlowPair[];
    connectorStyle?: string;
    vertices?: SnapshotPoint[];
    labels?: SnapshotLinkLabel[];
}

export interface FlowCapabilitySchema {
    schemaVersion: number;
    capabilities: Record<SnapshotCellKind, CellKindCapability>;
}

// A snapshot is the capability schema plus the diagram's cells, so a field added to the schema is
// a field a snapshot must also carry — extending makes that a compile error rather than a drift.
export interface FlowGraphSnapshot extends FlowCapabilitySchema {
    cells: SnapshotCell[];
}

// The capability half of a snapshot, readable without a diagram: the table is static, so an agent
// can learn allowed values (icon names, connector styles) before any diagram exists to inspect.
export function describeCapabilities(): string {
    const schema: FlowCapabilitySchema = {
        schemaVersion: SNAPSHOT_SCHEMA_VERSION,
        capabilities: buildCapabilityTable(),
    };
    return JSON.stringify(schema);
}

export function inspectGraphJson(request: InspectGraphJsonRequest): string {
    const graph = createGraph(request.graphJson);
    const catalog = new Map<string, EntityCatalogEntry>();
    for (const entry of request.entityCatalog ?? []) {
        catalog.set(entry.entityId, entry);
    }

    const cells = graph.getCells().map(cell => readCell(cell, catalog));
    assignTailTargets(cells);
    const snapshot: FlowGraphSnapshot = {
        schemaVersion: SNAPSHOT_SCHEMA_VERSION,
        cells,
        capabilities: buildCapabilityTable(),
    };
    return JSON.stringify(snapshot);
}

// How far a tail tip may sit from a cell's box and still count as pointing at it. Agent-aimed tails
// land exactly on an edge; a hand-dragged one lands wherever the user dropped it, so this is slack
// for "close enough to read as pointing at that shape", not a hit test.
const TAIL_TARGET_TOLERANCE = 8;

// Which cell each callout's tail rests on, derived from the tip rather than persisted with it. The
// tip is the only durable record of an aim — the manual tail drag writes nothing else — so
// recomputing it here means a callout the user drags away from its target simply stops reporting
// one, instead of asserting an aim that is no longer on the canvas. Reported only on a unique
// match: an ambiguous tip is left unreported rather than resolved by guesswork.
function assignTailTargets(cells: SnapshotCell[]): void {
    const boxes = cells.filter(cell => cell.position !== undefined && cell.size !== undefined);
    for (const callout of cells) {
        if (callout.kind !== 'callout' || !callout.tailTip) {
            continue;
        }
        const matches = boxes.filter(candidate =>
            candidate.cellId !== callout.cellId
            && distanceToBox(callout.tailTip!, candidate) <= TAIL_TARGET_TOLERANCE);
        if (matches.length === 1) {
            callout.tailTargetCellId = matches[0].cellId;
        }
    }
}

// Zero when the point is inside the box, otherwise the gap to its nearest edge.
function distanceToBox(point: SnapshotPoint, cell: SnapshotCell): number {
    const { x, y } = cell.position!;
    const { width, height } = cell.size!;
    const dx = Math.max(x - point.x, 0, point.x - (x + width));
    const dy = Math.max(y - point.y, 0, point.y - (y + height));
    return Math.hypot(dx, dy);
}

function readCell(cell: joint.dia.Cell, catalog: Map<string, EntityCatalogEntry>): SnapshotCell {
    const kind = classifyCell(cell);
    const snapshot: SnapshotCell = {
        cellId: String(cell.id),
        kind,
        cellType: String(cell.get('type') ?? 'unknown'),
    };

    if (cell instanceof joint.dia.Element) {
        // Attribute-level reads: a legacy cell serialized without position/size must inspect as
        // "absent", not throw the way the position()/size() accessors do.
        const position = cell.get('position') as { x?: number; y?: number } | undefined;
        if (position && typeof position.x === 'number' && typeof position.y === 'number') {
            snapshot.position = { x: position.x, y: position.y };
        }
        const size = cell.get('size') as { width?: number; height?: number } | undefined;
        if (size && typeof size.width === 'number' && typeof size.height === 'number') {
            snapshot.size = { width: size.width, height: size.height };
        }
    }

    switch (kind) {
        case 'entity':
        case 'entityGroup':
        case 'thirdParty':
            readEntityLikeNode(cell as Node, kind, snapshot, catalog);
            break;
        case 'textBox':
            readTextBox(cell as TextBox, snapshot);
            break;
        case 'callout':
            readCallout(cell as Callout, snapshot);
            break;
        case 'rectangle':
            snapshot.borderColor = orUndefined((cell as DrawnRectangle).getBorderColor());
            break;
        case 'icon':
            snapshot.iconName = orUndefined((cell as IconElement).getIconName());
            snapshot.shapeColor = orUndefined((cell as IconElement).getShapeColor());
            break;
        case 'line':
            readDrawnLine(cell as DrawnLine, snapshot);
            break;
        case 'flow':
            readFlowLink(cell as joint.shapes.standard.Link, snapshot);
            break;
        default:
            break;
    }

    return snapshot;
}

function readEntityLikeNode(
    node: Node,
    kind: SnapshotCellKind,
    snapshot: SnapshotCell,
    catalog: Map<string, EntityCatalogEntry>,
): void {
    const entityIds: string[] = Array.isArray(node.entityIds) ? node.entityIds.map(String) : [];
    snapshot.entityIds = entityIds;
    snapshot.entities = entityIds.map(entityId => {
        const entry = catalog.get(entityId);
        // An id the catalog cannot resolve is reported with its id alone — a stale or foreign
        // reference the agent should see, never fabricated metadata.
        if (!entry) {
            return { entityId };
        }
        const entity: SnapshotCellEntity = {
            entityId,
            label: entry.label,
            shape: entry.shape,
            countryCode: entry.countryCode,
        };
        if (entry.isExternal) {
            entity.isExternal = true;
        }
        return entity;
    });

    // Node.setLabel hard-wraps at 15 characters (and already discards the original spacing), so
    // the snapshot reports the whitespace-normalized user text — the value a later setLabel
    // round-trips — rather than the wrapped rendering.
    snapshot.primaryLabel = unwrapNodeLabel(node.attr('label/text') as string | undefined);
    snapshot.secondaryLabel = unwrapNodeLabel(node.attr('label2/text') as string | undefined);
    snapshot.textColor = orUndefined(node.attr('label/fill') as string | undefined);

    if (node instanceof EntityGroup) {
        // EntityGroup paints body1 and body2 (differently for Mixed jurisdiction); report both
        // rather than collapsing them the way the editor's selection read does.
        snapshot.backgroundColor = orUndefined(node.attr('body1/fill') as string | undefined);
        snapshot.backgroundColor2 = orUndefined(node.attr('body2/fill') as string | undefined);
        const groupId = node.prop('groupId') as string | undefined;
        snapshot.groupId = orUndefined(groupId);
        snapshot.groupLabel = orUndefined(node.prop('groupLabel') as string | undefined);
        snapshot.isManagedGroup = !!groupId;
    } else {
        snapshot.backgroundColor = orUndefined(node.attr('body/fill') as string | undefined);
    }

    if (kind === 'thirdParty') {
        snapshot.isLinkedToProjectEntities = entityIds.length > 0;
    }
}

function readTextBox(textBox: TextBox, snapshot: SnapshotCell): void {
    // TextBox/Callout setLabel never wraps, so newlines here are user-authored — report raw.
    snapshot.text = orUndefined(textBox.attr('label/text') as string | undefined);
    snapshot.backgroundColor = orUndefined(textBox.attr('body/fill') as string | undefined);
    snapshot.textColor = orUndefined(textBox.attr('label/fill') as string | undefined);
    snapshot.borderColor = orUndefined(textBox.attr('body/stroke') as string | undefined);
}

function readCallout(callout: Callout, snapshot: SnapshotCell): void {
    snapshot.text = orUndefined(callout.attr('label/text') as string | undefined);
    snapshot.backgroundColor = orUndefined(callout.attr('body/fill') as string | undefined);
    snapshot.textColor = orUndefined(callout.attr('label/fill') as string | undefined);
    const tailTip = callout.prop('tailTip') as SnapshotPoint | undefined;
    if (tailTip && Number.isFinite(tailTip.x) && Number.isFinite(tailTip.y)) {
        // Persisted local to the callout; reported in graph coordinates, so the tip can be compared
        // with — and aimed at — the positions of the other cells in this same snapshot.
        snapshot.tailTip = tailTipToGraph(tailTip, snapshot.position ?? { x: 0, y: 0 });
    }
}

function readDrawnLine(line: DrawnLine, snapshot: SnapshotCell): void {
    snapshot.strokeColor = orUndefined(line.getStrokeColor());
    snapshot.strokeWidth = line.getStrokeWidth();
    readLinkEndpoints(line, snapshot);
    snapshot.labels = readLinkLabels(line);
}

function readFlowLink(link: joint.shapes.standard.Link, snapshot: SnapshotCell): void {
    // null for legacy 'standard.Link' cells — cellType still identifies them.
    snapshot.flowType = flowTypeNameForLinkClass(snapshot.cellType);
    // Effective colour, for description only: the capability table declares link colour a
    // function of flowType, never editable.
    snapshot.color = orUndefined(link.attr('line/stroke') as string | undefined);
    readLinkEndpoints(link, snapshot);

    // Storage keeps one flow on the cell itself and the rest in `mergedFlows`, because the cell has
    // to be one of them to carry their shared rendering. That asymmetry is a storage detail, not a
    // fact about the diagram: an arrow drawing several flows has no primary one. Normalize it away
    // here — report the single flow directly, or all of them as peers, never a mix.
    const mergedFlows = getMergedFlows(link);
    if (mergedFlows.length > 0) {
        snapshot.representedFlows = [
            {
                sourceEntityId: String(link.prop('sourceEntityId')),
                targetEntityId: String(link.prop('targetEntityId')),
                stepId: asOptionalString(link.prop('stepId')),
            },
            ...mergedFlows.map(flow => ({
                sourceEntityId: flow.sourceEntityId,
                targetEntityId: flow.targetEntityId,
                stepId: flow.stepId,
            })),
        ];
    } else {
        snapshot.sourceEntityId = orUndefined(asOptionalString(link.prop('sourceEntityId')));
        snapshot.targetEntityId = orUndefined(asOptionalString(link.prop('targetEntityId')));
        // Preserve empty: absent means visual-only, while present but malformed means orphaned.
        snapshot.stepId = asOptionalString(link.prop('stepId'));
    }
    if (link.prop('hiddenSameNodeLink') === true) {
        snapshot.hidden = true;
    }
    // Read the persisted property with the editor's fallback. getConnectorStyle only exists
    // after applyConnectorStyleMixin runs, and applying the mixin here would *write* a default
    // onto the link — the inspector must not mutate what it reads.
    snapshot.connectorStyle = asOptionalString(link.prop('connectorStyle')) || 'sharp';

    const vertices = link.vertices();
    if (vertices.length > 0) {
        snapshot.vertices = vertices.map(vertex => ({ x: vertex.x, y: vertex.y }));
    }
    snapshot.labels = readLinkLabels(link);
}

function readLinkEndpoints(link: joint.dia.Link, snapshot: SnapshotCell): void {
    for (const end of ['source', 'target'] as const) {
        const cellId = getLinkEndId(link, end);
        if (cellId !== undefined) {
            snapshot[end === 'source' ? 'sourceCellId' : 'targetCellId'] = cellId;
            continue;
        }
        const point = link.get(end) as { x?: number; y?: number } | undefined;
        if (point && typeof point.x === 'number' && typeof point.y === 'number') {
            snapshot[end === 'source' ? 'sourcePoint' : 'targetPoint'] = { x: point.x, y: point.y };
        }
    }
}

function readLinkLabels(link: joint.dia.Link): SnapshotLinkLabel[] {
    const labels = link.labels() ?? [];
    const result: SnapshotLinkLabel[] = [];
    labels.forEach((label, index) => {
        // An empty text label is hidden, not removed (updateLinkProperty('primaryLabel', '')) —
        // treat the placeholder as no label at all. The index stays the label's position in the
        // raw labels[] array so patches can address surviving labels stably.
        const attrs = (label as { attrs?: { rect?: { visibility?: string }; text?: { text?: string } } }).attrs;
        if (attrs?.rect?.visibility === 'hidden') {
            return;
        }
        if (isIconLabel(label)) {
            result.push({
                index,
                kind: 'icon',
                iconName: orUndefined(asOptionalString((label as { iconName?: unknown }).iconName)),
            });
            return;
        }
        result.push({ index, kind: 'text', text: attrs?.text?.text ?? '' });
    });
    return result;
}

// Node labels: collapse the wrap (and any spacing around it) back to single spaces.
function unwrapNodeLabel(value: string | undefined): string | undefined {
    if (!value) {
        return undefined;
    }
    const unwrapped = value.replace(/\s*\n\s*/g, ' ').trim();
    return unwrapped.length > 0 ? unwrapped : undefined;
}

function orUndefined(value: string | undefined | null): string | undefined {
    return value ? value : undefined;
}

function asOptionalString(value: unknown): string | undefined {
    return value === null || value === undefined ? undefined : String(value);
}
